import type { Delta } from 'quill/core'

import Quill from 'quill'
import 'quill/dist/quill.snow.css'
import { forwardRef, useEffect, useLayoutEffect, useRef } from 'react'

const toolbarOptions = [
  ['bold', 'italic', 'underline', 'strike'], // toggled buttons
  ['blockquote', 'code-block'],
  ['link', 'image', 'video', 'formula'],

  // [{ header: 1 }, { header: 2 }], // custom button values
  [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
  [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
  [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
  [{ direction: 'rtl' }], // text direction

  [{ size: ['small', false, 'large', 'huge'] }], // custom dropdown
  [{ header: [1, 2, 3, 4, 5, 6, false] }],

  [{ color: [] }, { background: [] }], // dropdown with defaults from theme
  [{ font: [] }],
  [{ align: [] }],

  ['clean'], // remove formatting button
]

export interface QuillEditorProps {
  defaultValue?: Delta
  onSelectionChange?: (...args: Delta[]) => void
  onTextChange?: (...args: Delta[]) => void
  readonly?: boolean
}

export const QuillEditor = forwardRef<null | Quill, QuillEditorProps>(
  // eslint-disable-next-line react/prop-types
  ({ defaultValue, onSelectionChange, onTextChange, readonly }, ref) => {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const defaultValueRef = useRef(defaultValue)
    const onTextChangeRef = useRef(onTextChange)
    const onSelectionChangeRef = useRef(onSelectionChange)

    useLayoutEffect(() => {
      onTextChangeRef.current = onTextChange
      onSelectionChangeRef.current = onSelectionChange
    }, [onTextChange, onSelectionChange])

    useEffect(() => {
      if (ref && 'current' in ref && ref.current) {
        ref.current.enable(!readonly)
      }
    }, [ref, readonly])

    useEffect(() => {
      const container = containerRef.current
      if (!container) return

      const editorContainer = container.appendChild(
        document.createElement('div'),
      )
      const quill = new Quill(editorContainer, {
        modules: {
          toolbar: toolbarOptions,
        },
        theme: 'snow',
      })

      if (ref && 'current' in ref) {
        ref.current = quill
      }

      if (defaultValueRef.current) {
        quill.setContents(defaultValueRef.current)
      }

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error - quill typings are not up to date
      quill.on(Quill.events.TEXT_CHANGE, (...args) =>
        onTextChangeRef.current?.(...args),
      )

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error - quill typings are not up to date
      quill.on(Quill.events.SELECTION_CHANGE, (...args) =>
        onSelectionChangeRef.current?.(...args),
      )

      return () => {
        if (ref && 'current' in ref) {
          ref.current = null
        }
        container.innerHTML = ''
      }
    }, [ref])

    return (
      <div
        className="border [&>.ql-container>.ql-editor]:min-h-[10rem] [&>.ql-toolbar]:bg-default-100"
        ref={containerRef}
      ></div>
    )
  },
)

QuillEditor.displayName = 'QuillEditor'

export default QuillEditor
